import React from "react";
import { Text, View } from "react-native";
import moment from "moment";
import tw from "twrnc";

import Button from "@/components/settings/Button";
import { colors, radius, text } from "@/components/settings/theme";
import { getLanguage, t, tc } from "@/localization";
import { formatEntryDate } from "./logic";

// The What's new list, shared by the header dropdown and the screen: rows
// separated by hairlines. Entries unread when the view opened (`fresh`) get
// the brand tint and a "New" pill; read ones are plain rows.
// `gutter` is the rows' side padding (the tint reaches the edges).
// Entries come from entries.json (or the same feed fetched later) and are
// translated in localization/es/whatsnew.json.

// logic.js stays free of the app's languages (the What's New check runs it in
// plain Node), so other languages format the date here: "13 oct." this year,
// "13 oct. 2025" before.
const entryDate = (date) => {
  if (getLanguage() === "en") return formatEntryDate(date);
  const thisYear = Number(date.slice(0, 4)) === new Date().getUTCFullYear();
  return moment(date, "YYYY-MM-DD").format(
    thisYear ? t("MMM D") : t("MMM D, YYYY")
  );
};

const NewPill = () => (
  <View
    style={{
      backgroundColor: colors.brand,
      borderRadius: radius.pill,
      paddingHorizontal: 8,
      paddingVertical: 1,
      marginRight: 8,
    }}
  >
    <Text style={[text.hint, { color: colors.white, fontWeight: "700" }]}>
      {tc("pill", "New")}
    </Text>
  </View>
);

export const WhatsNewRow = ({
  entry,
  fresh,
  first,
  compact,
  gutter,
  canTry,
  onTry,
  onLearnMore,
}) => {
  const showTry = canTry(entry);
  return (
    <View
      testID={`whats-new-${entry.id}`}
      style={{
        backgroundColor: fresh ? colors.brandTint : "transparent",
        borderTopWidth: first ? 0 : 1,
        borderTopColor: colors.divider,
        paddingHorizontal: gutter,
        paddingVertical: compact ? 14 : 18,
      }}
    >
      <View style={[tw`flex-row items-center`, { marginBottom: 6 }]}>
        {fresh && <NewPill />}
        <Text style={text.hint}>
          {entryDate(entry.date)}
          {entry.tag ? ` · ${t(entry.tag)}` : ""}
        </Text>
      </View>
      <Text
        accessibilityRole="header"
        style={compact ? text.bodyStrong : text.sectionTitle}
      >
        {t(entry.title)}
      </Text>
      <Text style={[text.body, { marginTop: 4 }]}>{t(entry.body)}</Text>
      <View style={[tw`flex-row flex-wrap items-center`, { marginTop: 10 }]}>
        {showTry && (
          <Button
            text={t(entry.try.label)}
            size="sm"
            fullWidth={false}
            onPress={() => onTry(entry)}
            style={{ marginRight: 8 }}
          />
        )}
        <Button
          text={t(entry.link.label)}
          variant="tertiary"
          size="sm"
          iconRight="open-outline"
          onPress={() => onLearnMore(entry)}
          accessibilityLabel={t("{label}, opens eventlive.pro", {
            label: t(entry.link.label),
          })}
          style={!showTry && { marginLeft: -8 }}
        />
      </View>
    </View>
  );
};

const WhatsNewList = ({ entries, fresh, compact, gutter = 0, actions }) => {
  if (!entries.length) {
    return (
      <Text
        style={[text.hint, { paddingHorizontal: gutter, paddingVertical: 14 }]}
      >
        {t(
          "Nothing new yet. New features for your business will show up here."
        )}
      </Text>
    );
  }
  return (
    <View>
      {entries.map((entry, i) => (
        <WhatsNewRow
          key={entry.id}
          entry={entry}
          fresh={fresh.has(entry.id)}
          first={i === 0}
          compact={compact}
          gutter={gutter}
          canTry={actions.canTry}
          onTry={actions.runTry}
          onLearnMore={actions.openLink}
        />
      ))}
    </View>
  );
};

export default WhatsNewList;
